import { useMemo } from "react";
import { Box } from "~/components/Box";
import { EmptyState } from "~/components/EmptyState";
import { Grid } from "~/components/Grid";
import { LazyList } from "~/components/LazyList";
import { Skeleton } from "~/components/Skeleton";
import { DimensionalResponse, TravelAnalyticsPageFilters } from "../types";
import {
  asCurrency,
  asPercent,
  currentYearMonth,
  formatYearMonth,
  getMonthsInRange,
  startOfYearYearMonth,
} from "../utils";
import { TableBodyCell, TableHeaderCell, TableRow } from "./dataTable";
import { PaginatedTableCard } from "./PaginatedTableCard";

export interface DimensionalTableProps {
  filters?: TravelAnalyticsPageFilters;
  data?: DimensionalResponse;
  isFetching: boolean;
  page: number;
  onPageChange: (page: number) => void;
  search: string;
  onSearchChange: (search: string) => void;

  title: string;
  searchPlaceholder: string;
  firstColumnHeader: string;
  emptyMessage: string;
}

const columnsTemplate = (monthCount: number): string =>
  `minmax(220px, 360px) repeat(${monthCount}, minmax(110px, 1fr)) repeat(2, minmax(150px, 1fr))`;

export const DimensionalTable = ({
  filters,
  data,
  isFetching,
  page,
  onPageChange,
  search,
  onSearchChange,
  title,
  searchPlaceholder,
  firstColumnHeader,
  emptyMessage,
}: DimensionalTableProps) => {
  const items = data?.data ?? [];

  const monthKeys = useMemo(() => {
    if (isFetching || !data) {
      if (filters) {
        return getMonthsInRange(filters.fromYearMonth, filters.toYearMonth);
      }
      return getMonthsInRange(startOfYearYearMonth(), currentYearMonth());
    }
    const keys = new Set<string>();
    items.forEach((item) => {
      Object.keys(item.months).forEach((key) => keys.add(key));
    });
    return Array.from(keys).sort();
  }, [items, isFetching, data, filters]);

  const renderBody = () => {
    return (
      <Box css={{ overflowX: "auto" }}>
        <Grid style={{ gridTemplateColumns: columnsTemplate(monthKeys.length) }}>
          <TableHeaderCell sticky>{firstColumnHeader}</TableHeaderCell>
          {monthKeys.map((key) => (
            <TableHeaderCell key={key} numeric>
              {formatYearMonth(key)}
            </TableHeaderCell>
          ))}
          <TableHeaderCell numeric>Total geral</TableHeaderCell>
          <TableHeaderCell numeric>% representatividade</TableHeaderCell>

          <LazyList
            as="div"
            itemAs="div"
            css={{ display: "contents", "& > div": { display: "contents" } }}
            isLoading={isFetching || !data}
            items={items}
            skeletonQuantity={10}
            EmptyComponent={
              <div style={{ gridColumn: "1 / -1" }}>
                <EmptyState>{emptyMessage}</EmptyState>
              </div>
            }
            SkeletonComponent={
              <TableRow>
                <TableBodyCell bold sticky>
                  <Skeleton css={{ width: "80%", height: "$5" }} />
                </TableBodyCell>
                {monthKeys.map((key) => (
                  <TableBodyCell key={`skeleton-month-${key}`} numeric>
                    <Skeleton css={{ width: "60px", height: "$5" }} />
                  </TableBodyCell>
                ))}
                <TableBodyCell numeric bold>
                  <Skeleton css={{ width: "60px", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "40px", height: "$5" }} />
                </TableBodyCell>
              </TableRow>
            }
            render={(item) => (
              <TableRow key={item.uuid}>
                <TableBodyCell bold sticky>
                  {item.name}
                </TableBodyCell>
                {monthKeys.map((key) => (
                  <TableBodyCell key={key} numeric>
                    {asCurrency(item.months[key] ?? 0)}
                  </TableBodyCell>
                ))}
                <TableBodyCell numeric bold>
                  {asCurrency(item.total)}
                </TableBodyCell>
                <TableBodyCell numeric>{asPercent(item.representativeness)}</TableBodyCell>
              </TableRow>
            )}
          />
        </Grid>
      </Box>
    );
  };

  return (
    <PaginatedTableCard
      title={title}
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      page={page}
      totalPages={data?.pagination.totalPages ?? 0}
      onPageChange={onPageChange}
      hasData={!!data && items.length > 0}
    >
      {renderBody()}
    </PaginatedTableCard>
  );
};

DimensionalTable.displayName = "DimensionalTable";
